import React, { useState, useEffect } from 'react';
import { X, MessageSquare, Award, Star, Sparkles, TrendingUp, Users, Home } from 'lucide-react';
import { forumAPI, authAPI } from '../services/api';

const categories = [
  { id: 'discussion', label: 'Discussion', icon: MessageSquare, color: '#8B5CF6' },
  { id: 'question', label: 'Question', icon: Award, color: '#22C55E' },
  { id: 'review', label: 'Review', icon: Star, color: '#EAB308' },
  { id: 'reaction_request', label: 'Request', icon: Sparkles, color: '#F97316' },
  { id: 'news', label: 'News', icon: TrendingUp, color: '#3B82F6' },
  { id: 'meme', label: 'Meme', icon: Users, color: '#EC4899' },
  { id: 'other', label: 'Other', icon: Home, color: '#6B7280' }
];

const CreateThreadModal = ({ isOpen, onClose, onSuccess, linkedContent = null }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('discussion');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    if (isOpen) {
      const token = localStorage.getItem('authToken');
      if (token) {
        authAPI.getCurrentUser().then(r => r.success && setCurrentUser(r.account)).catch(() => setCurrentUser(null));
      } else {
        setCurrentUser(null);
      }
    }
  }, [isOpen]);

  const handleTagKeyDown = (e) => {
    if (e.key === ' ' || e.key === ',' || e.key === 'Enter') {
      e.preventDefault();
      const newTag = tagInput.trim();
      if (newTag && !tags.includes(newTag)) setTags([...tags, newTag]);
      setTagInput('');
    } else if (e.key === 'Backspace' && !tagInput && tags.length > 0) {
      setTags(tags.slice(0, -1));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!title.trim() || !content.trim()) return setError('Please fill in both title and content');
    if (!currentUser) return setError('Please sign in to create a thread');

    try {
      setSubmitting(true);
      const response = await forumAPI.createThread({
        title: title.trim(), content: content.trim(), category, tags,
        linkedContent: linkedContent || { type: 'general' }
      });
      if (response.success) {
        setTitle(''); setContent(''); setCategory('discussion'); setTags([]); setTagInput('');
        onSuccess && onSuccess(response.thread);
        onClose();
      } else {
        setError(response.error || 'Failed to create thread');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create thread');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px'
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#111827', borderRadius: '16px', maxWidth: '640px', width: '100%',
          maxHeight: '90vh', overflow: 'auto', border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '18px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <h2 style={{ color: '#FFFFFF', fontSize: '16px', fontWeight: '700', margin: 0 }}>Create Thread</h2>
          <button onClick={onClose} style={{
            width: '32px', height: '32px', borderRadius: '8px', border: 'none',
            background: 'rgba(255,255,255,0.05)', color: '#9CA3AF', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}><X size={16} /></button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '20px' }}>
          {error && (
            <div style={{
              padding: '10px 12px', borderRadius: '8px', background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)', color: '#EF4444', fontSize: '12px', marginBottom: '16px'
            }}>{error}</div>
          )}

          {/* Title */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#9CA3AF', marginBottom: '6px' }}>
              Title *
            </label>
            <input
              type="text" value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="What's on your mind?" maxLength={200}
              style={{
                width: '100%', padding: '10px 12px', borderRadius: '10px',
                border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.03)',
                color: '#FFFFFF', fontSize: '13px', outline: 'none', boxSizing: 'border-box'
              }}
            />
            <div style={{ fontSize: '10px', color: '#6B7280', marginTop: '4px', textAlign: 'right' }}>{title.length}/200</div>
          </div>

          {/* Category */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#9CA3AF', marginBottom: '8px' }}>
              Category *
            </label>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {categories.map(cat => {
                const Icon = cat.icon;
                const isSelected = category === cat.id;
                return (
                  <button
                    key={cat.id} type="button" onClick={() => setCategory(cat.id)}
                    style={{
                      padding: '8px 12px', borderRadius: '8px',
                      border: `1px solid ${isSelected ? cat.color + '50' : 'rgba(255,255,255,0.08)'}`,
                      background: isSelected ? cat.color + '15' : 'transparent',
                      color: isSelected ? cat.color : '#9CA3AF',
                      fontSize: '11px', fontWeight: '500', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: '5px', transition: 'all 0.15s'
                    }}
                  >
                    <Icon size={12} /> {cat.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tags */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#9CA3AF', marginBottom: '6px' }}>
              Tags (optional)
            </label>
            {tags.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                {tags.map((tag, i) => (
                  <span key={i} style={{
                    display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 8px',
                    borderRadius: '6px', background: 'rgba(139, 92, 246, 0.15)',
                    color: '#8B5CF6', fontSize: '11px', fontWeight: '500'
                  }}>
                    {tag}
                    <button type="button" onClick={() => setTags(tags.filter((_, idx) => idx !== i))} style={{
                      background: 'none', border: 'none', color: '#8B5CF6', cursor: 'pointer', padding: '0', fontSize: '12px'
                    }}>×</button>
                  </span>
                ))}
              </div>
            )}
            <input
              type="text" value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={handleTagKeyDown}
              placeholder="Type and press space to add tags"
              style={{
                width: '100%', padding: '8px 12px', borderRadius: '8px',
                border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.03)',
                color: '#FFFFFF', fontSize: '12px', outline: 'none', boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Content */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#9CA3AF', marginBottom: '6px' }}>
              Content *
            </label>
            <textarea
              value={content} onChange={(e) => setContent(e.target.value)}
              placeholder="Share your thoughts with the community..." maxLength={10000}
              style={{
                width: '100%', minHeight: '180px', padding: '12px', borderRadius: '10px',
                border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.03)',
                color: '#FFFFFF', fontSize: '13px', fontFamily: 'inherit', resize: 'vertical',
                outline: 'none', lineHeight: '1.6', boxSizing: 'border-box'
              }}
            />
            <div style={{ fontSize: '10px', color: '#6B7280', marginTop: '4px', textAlign: 'right' }}>{content.length}/10,000</div>
          </div>

          {/* Linked Content */}
          {linkedContent && linkedContent.type !== 'general' && (
            <div style={{
              padding: '10px 12px', borderRadius: '8px', background: 'rgba(139, 92, 246, 0.1)',
              border: '1px solid rgba(139, 92, 246, 0.2)', marginBottom: '16px'
            }}>
              <div style={{ fontSize: '10px', color: '#9CA3AF', marginBottom: '4px' }}>Linked to:</div>
              <div style={{ fontSize: '12px', color: '#8B5CF6', fontWeight: '600' }}>{linkedContent.title || linkedContent.type}</div>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', paddingTop: '16px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <button type="button" onClick={onClose} disabled={submitting} style={{
              padding: '10px 18px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.1)',
              background: 'transparent', color: '#9CA3AF', fontSize: '13px', fontWeight: '500', cursor: 'pointer'
            }}>Cancel</button>
            <button type="submit" disabled={!title.trim() || !content.trim() || submitting} style={{
              padding: '10px 22px', borderRadius: '10px', border: 'none',
              background: (!title.trim() || !content.trim() || submitting) ? '#374151' : 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
              color: '#FFFFFF', fontSize: '13px', fontWeight: '600',
              cursor: (!title.trim() || !content.trim() || submitting) ? 'not-allowed' : 'pointer',
              boxShadow: (!title.trim() || !content.trim() || submitting) ? 'none' : '0 4px 12px rgba(139, 92, 246, 0.3)'
            }}>{submitting ? 'Creating...' : 'Create Thread'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateThreadModal;
